// /src/pages/Profile/Profile.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";
import { db, storage } from "../../firebase";
import { doc, getDoc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import SubscriptionPanel from "../../components/SubscriptionPanel";
import "./profile.css";

const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
console.log("🔑 [Profile] Stripe Key:", stripeKey ? `${stripeKey.substring(0, 20)}...` : "NOT FOUND");
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;
console.log("💳 [Profile] Stripe Promise:", stripePromise ? "Loaded" : "NULL - KEY MISSING!");

// Account level hierarchy
const ACCOUNT_LEVELS = {
  "New Agent": { next: "Agent", order: 0 },
  "Agent": { next: "Leader", order: 1 },
  "Leader": { next: null, order: 2 }, // Broker is manual only
  "Broker": { next: null, order: 3 },
  "Admin": { next: null, order: 4 }
};

function ProfileSection({ userData, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({
    fullName: userData?.fullName || userData?.name || "",
    email: userData?.email || "",
    phone: userData?.phone || "",
    smdName: userData?.smdName || "",
    photoURL: userData?.photoURL || ""
  });

  useEffect(() => {
    setForm({
      fullName: userData?.fullName || userData?.name || "",
      email: userData?.email || "",
      phone: userData?.phone || "",
      smdName: userData?.smdName || "",
      photoURL: userData?.photoURL || ""
    });
  }, [userData]);

  const handleSave = async () => {
    try {
      await onUpdate(form);
      setEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      alert("Failed to update profile. Please try again.");
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const storageRef = ref(storage, `profile-photos/${userData.uid}_${Date.now()}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on(
        "state_changed",
        null,
        (error) => {
          console.error("Upload error:", error);
          setUploading(false);
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setForm({ ...form, photoURL: downloadURL });
          setUploading(false);
        }
      );
    } catch (error) {
      console.error("Error uploading photo:", error);
      setUploading(false);
    }
  };

  const initials = form.fullName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

  return (
    <div className="glass-section">
      <div className="section-header">
        <h3 className="section-title">
          <span className="title-icon">👤</span>
          Profile
        </h3>
        {!editing ? (
          <button className="btn-primary" onClick={() => setEditing(true)}>
            Edit Profile
          </button>
        ) : (
          <div className="button-group">
            <button className="btn-primary" onClick={handleSave}>
              Save
            </button>
            <button className="btn-secondary" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        )}
      </div>

      <div className="profile-content">
        <div className="profile-photo-section">
          <div className="profile-photo">
            {form.photoURL ? (
              <img src={form.photoURL} alt="Profile" />
            ) : (
              <div className="profile-initials">{initials}</div>
            )}
          </div>
          {editing && (
            <div className="photo-upload">
              <label className="upload-btn">
                {uploading ? "Uploading..." : "Change Photo"}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  disabled={uploading}
                  style={{ display: "none" }}
                />
              </label>
            </div>
          )}
        </div>

        <div className="profile-fields">
          <div className="field-row">
            <div className="field-group">
              <label>Full Name</label>
              <input
                type="text"
                className="input-field"
                value={form.fullName}
                onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                disabled={!editing}
              />
            </div>
            <div className="field-group">
              <label>Email</label>
              <input
                type="email"
                className="input-field"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={!editing}
              />
            </div>
          </div>

          <div className="field-row">
            <div className="field-group">
              <label>Phone</label>
              <input
                type="tel"
                className="input-field"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                disabled={!editing}
              />
            </div>
            <div className="field-group">
              <label>SMD (Senior Marketing Director)</label>
              <input
                type="text"
                className="input-field"
                value={form.smdName}
                onChange={(e) => setForm({ ...form, smdName: e.target.value })}
                disabled={!editing}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AccountLevelSection({ userData, onRequestUpgrade }) {
  const [requesting, setRequesting] = useState(false);
  const currentLevel = userData?.plan || "New Agent";
  const nextLevel = ACCOUNT_LEVELS[currentLevel]?.next;

  const handleUpgrade = async () => {
    if (!nextLevel) return;
    
    setRequesting(true);
    try {
      await onRequestUpgrade(nextLevel);
      alert(`Upgrade request to ${nextLevel} submitted successfully!`);
    } catch (error) {
      console.error("Error requesting upgrade:", error);
      alert("Failed to submit upgrade request. Please try again.");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className="glass-section">
      <div className="section-header">
        <h3 className="section-title">
          <span className="title-icon">⭐</span>
          Account Level
        </h3>
      </div>

      <div className="account-level-content">
        <div className="current-level">
          <div className="level-badge">
            <div className="level-name">{currentLevel}</div>
            <div className="level-description">Current Account Level</div>
          </div>
        </div>

        {/* {nextLevel && (
          <div className="upgrade-section">
            <div className="upgrade-info">
              <div className="upgrade-arrow">↓</div>
              <div className="next-level">Next Level: <strong>{nextLevel}</strong></div>
            </div>
            <button
              className="btn-upgrade"
              onClick={handleUpgrade}
              disabled={requesting}
            >
              {requesting ? "Requesting..." : `Request Upgrade to ${nextLevel}`}
            </button>
          </div>
        )}

        {!nextLevel && currentLevel !== "Admin" && (
          <div className="max-level-info">
            <p>🎉 You've reached the maximum self-service level!</p>
            <p className="info-text">
              Broker level can only be assigned by administrators.
            </p>
          </div>
        )} */}
      </div>
    </div>
  );
}

function MyEventTicketSection({ userData }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTickets();
  }, [userData]);

  const loadTickets = async () => {
    if (!userData?.uid) return;
    
    try {
      // Query tickets where user is the owner
      const ticketsRef = collection(db, "tickets");
      const q = query(ticketsRef, where("ownerId", "==", userData.uid));
      const snapshot = await getDocs(q);
      
      const ticketList = [];
      snapshot.forEach((doc) => {
        ticketList.push({ id: doc.id, ...doc.data() });
      });
      
      setTickets(ticketList);
    } catch (error) {
      console.error("Error loading tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const myTicket = tickets.find((t) => t.assignedTo === userData?.email || t.isMainTicket);
  const extraTickets = tickets.filter((t) => t.assignedTo !== userData?.email && !t.isMainTicket);

  return (
    <div className="glass-section">
      <div className="section-header">
        <h3 className="section-title">
          <span className="title-icon">🎟️</span>
          My Event Tickets
        </h3>
      </div>

      {loading ? (
        <div className="loading-state">Loading tickets...</div>
      ) : tickets.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🎫</div>
          <p>No event tickets found</p>
          <p className="empty-text">Purchase tickets to see them here</p>
        </div>
      ) : (
        <div className="tickets-content">
          {myTicket && (
            <div className="my-ticket">
              <div className="ticket-label">My Ticket</div>
              <div className="ticket-card">
                <div className="ticket-event">{myTicket.eventName || "Upcoming Event"}</div>
                <div className="ticket-info">
                  <div className="ticket-detail">
                    <span className="detail-label">Ticket #</span>
                    <span className="detail-value">{myTicket.ticketNumber || myTicket.id}</span>
                  </div>
                  <div className="ticket-detail">
                    <span className="detail-label">Type</span>
                    <span className="detail-value">{myTicket.type || "General"}</span>
                  </div>
                  <div className="ticket-detail">
                    <span className="detail-label">Status</span>
                    <span className={`status-badge ${myTicket.status?.toLowerCase()}`}>
                      {myTicket.status || "Active"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {extraTickets.length > 0 && (
            <div className="extra-tickets">
              <div className="ticket-label">Extra Tickets to Assign</div>
              <div className="tickets-grid">
                {extraTickets.map((ticket) => (
                  <div key={ticket.id} className="ticket-card-small">
                    <div className="ticket-number">#{ticket.ticketNumber || ticket.id.slice(-6)}</div>
                    <div className="ticket-type">{ticket.type || "General"}</div>
                    <div className="ticket-status">
                      {ticket.assignedTo ? `Assigned to ${ticket.assignedTo}` : "Unassigned"}
                    </div>
                    <button className="btn-assign">Assign</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PaymentMethodForm() {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [savedCard, setSavedCard] = useState(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    loadSavedCard();
  }, [user]);

  const loadSavedCard = async () => {
    if (!user?.uid) return;
    
    try {
      const docRef = doc(db, "stripe_customers", user.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.paymentMethod) {
          setSavedCard(data.paymentMethod);
        }
      }
    } catch (error) {
      console.error("Error loading saved card:", error);
    }
  };

  const handleSavePaymentMethod = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSaving(true);
    try {
      const cardElement = elements.getElement(CardElement);
      
      // Create payment method
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
      });

      if (error) {
        alert(error.message);
        setSaving(false);
        return;
      }

      // Save to Firestore
      const docRef = doc(db, "stripe_customers", user.uid);
      await setDoc(docRef, {
        paymentMethod: {
          id: paymentMethod.id,
          brand: paymentMethod.card.brand,
          last4: paymentMethod.card.last4,
          exp_month: paymentMethod.card.exp_month,
          exp_year: paymentMethod.card.exp_year,
        },
        updatedAt: new Date().toISOString()
      }, { merge: true });

      setSavedCard({
        brand: paymentMethod.card.brand,
        last4: paymentMethod.card.last4,
        exp_month: paymentMethod.card.exp_month,
        exp_year: paymentMethod.card.exp_year,
      });

      setEditing(false);
      alert("Payment method saved successfully!");
    } catch (error) {
      console.error("Error saving payment method:", error);
      alert("Failed to save payment method. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="payment-method-content">
      {savedCard && !editing ? (
        <div className="saved-card">
          <div className="card-display">
            <div className="card-brand">{savedCard.brand?.toUpperCase()}</div>
            <div className="card-number">•••• •••• •••• {savedCard.last4}</div>
            <div className="card-expiry">
              Expires {savedCard.exp_month}/{savedCard.exp_year}
            </div>
          </div>
          <button className="btn-secondary" onClick={() => setEditing(true)}>
            Update Card
          </button>
        </div>
      ) : (
        <form onSubmit={handleSavePaymentMethod} className="payment-form">
          <div className="card-element-wrapper">
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#fff",
                    "::placeholder": {
                      color: "rgba(255, 255, 255, 0.5)",
                    },
                  },
                  invalid: {
                    color: "#ff6b6b",
                  },
                },
              }}
            />
          </div>
          <div className="payment-notice">
            <span className="notice-icon">🔒</span>
            <p>This payment method is for CRM subscription access only. Event tickets use a separate payment system.</p>
          </div>
          <div className="button-group">
            <button
              type="submit"
              className="btn-primary"
              disabled={!stripe || saving}
            >
              {saving ? "Saving..." : "Save Payment Method"}
            </button>
            {savedCard && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}

function PaymentMethodSection() {
  return (
    <div className="glass-section">
      <div className="section-header">
        <h3 className="section-title">
          <span className="title-icon">💳</span>
          Payment Method
        </h3>
      </div>
      <Elements stripe={stripePromise}>
        <PaymentMethodForm />
      </Elements>
    </div>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUserData();
  }, [user]);

  const loadUserData = async () => {
    if (!user?.uid) return;
    
    try {
      const docRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        setUserData({ uid: user.uid, ...docSnap.data() });
      } else {
        // Initialize with auth data
        setUserData({
          uid: user.uid,
          email: user.email,
          fullName: user.displayName || "",
          plan: "New Agent"
        });
      }
    } catch (error) {
      console.error("Error loading user data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (formData) => {
    if (!user?.uid) return;
    
    const docRef = doc(db, "users", user.uid);
    await setDoc(docRef, {
      ...formData,
      name: formData.fullName,
      updatedAt: new Date().toISOString()
    }, { merge: true });
    
    setUserData({ ...userData, ...formData });
  };

  const handleRequestUpgrade = async (targetLevel) => {
    if (!user?.uid) return;
    
    // Create upgrade request
    const requestRef = doc(collection(db, "upgrade_requests"));
    await setDoc(requestRef, {
      userId: user.uid,
      userEmail: user.email,
      currentLevel: userData?.plan || "New Agent",
      requestedLevel: targetLevel,
      status: "pending",
      createdAt: new Date().toISOString()
    });
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <div className="profile-page">
        <div className="profile-header">
          <h1 className="page-title">My Profile</h1>
          <p className="page-subtitle">Manage your account settings and preferences</p>
        </div>

        <div className="profile-grid">
          <ProfileSection userData={userData} onUpdate={handleUpdateProfile} />
          <AccountLevelSection userData={userData} onRequestUpgrade={handleRequestUpgrade} />
          <MyEventTicketSection userData={userData} />
          <div className="glass-section">
            <div className="section-header">
              <h3 className="section-title">
                <span className="title-icon">💎</span>
                Subscription & Billing
              </h3>
            </div>
            <SubscriptionPanel />
          </div>
          {/* <PaymentMethodSection /> */}
        </div>
      </div>
    </Elements>
  );
}
